import { useMemo, useState } from 'react';

import type {
  ApBillDuplicateMatch,
  CreateApBill,
  CreateApBillLine,
  PaymentTerm,
} from '@dcc-erp/shared';
import {
  dateToInputValue,
  FA_CLEARING_DEFAULT_CODE,
  fmt,
  fmtDate,
  toNumber,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useCipProjects, useCoa, useVendors } from '../../lib/queries';
import { useApBillCheckDuplicate, useCreateApBill } from '../../lib/queries/ap';
import { usePaymentTerms } from '../../lib/queries/payment-terms';
import {
  modalBody,
  modalBox,
  modalError,
  modalFooter,
  modalHeader,
  modalLabel,
  modalOverlay,
  modalTitle,
} from '../ar/modal-style';
import { BranchSelect, DepartmentSelect } from '../dimensions';
import { SearchSelect } from '../SearchSelect';

const FA_CLEARING_CODE = FA_CLEARING_DEFAULT_CODE;

// =============================================================================
// Modal: Create AP Bill
// =============================================================================

export interface CreateBillModalProps {
  companyId: string;
  onClose: () => void;
  onOpenBill: (billId: string) => void;
}

export function CreateBillModal({ companyId, onClose, onOpenBill }: CreateBillModalProps) {
  const { data: vendors } = useVendors(companyId, true);
  const { data: coa } = useCoa(companyId, true);
  // AP Phase C1 — active payment-term presets feed the "เงื่อนไขชำระ" picker.
  const { data: paymentTerms = [] } = usePaymentTerms(companyId, true);
  // CIP Phase 4b — ACTIVE CIP projects for the optional "ผูกโครงการ CIP" selector.
  // Tagging a bill routes its work-cost debit to CIP_CLEARING for later capture.
  const { data: cipProjects = [] } = useCipProjects(companyId, { status: 'ACTIVE' });
  const create = useCreateApBill(companyId);

  // Stable idempotency key for this modal instance. Generated once when the
  // modal mounts (lazy useState initializer — avoids the react-hooks/purity
  // lint error that fires when crypto.randomUUID() is called during render).
  // The same key is reused if the user retries after a network error or
  // double-clicks submit, so the server can replay-gate the duplicate POST.
  // A fresh key is produced on the next open because the modal unmounts on
  // close (see: `{createOpen && <CreateBillModal …/>}` in ap.tsx).
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const today = new Date();
  const defaultDue = new Date(today);
  defaultDue.setDate(defaultDue.getDate() + 30);

  const [vendorId, setVendorId] = useState('');
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [billDate, setBillDate] = useState(dateToInputValue(today));
  const [dueDate, setDueDate] = useState(dateToInputValue(defaultDue));
  // AP Phase C1 — the selected payment-term preset code ('' = none). Choosing a
  // term auto-sets the due date to billDate + netDays (still overridable) and
  // surfaces the discount window; the code is sent in the create payload so the
  // server snapshots the term onto the bill.
  const [paymentTermCode, setPaymentTermCode] = useState<string>('');
  const [vatRate, setVatRate] = useState<number>(7);
  const [vendorInvoiceNo, setVendorInvoiceNo] = useState('');
  const [description, setDescription] = useState('');
  const [postJv, setPostJv] = useState<boolean>(true);
  // CIP Phase 4b — optional CIP project tag ('' = a normal bill, unchanged). When
  // set, cipProjectId is sent so the work-cost debit posts to CIP_CLEARING.
  const [cipProjectId, setCipProjectId] = useState<string>('');
  const [lines, setLines] = useState<CreateApBillLine[]>([
    { description: '', qty: 1, unitPrice: 0, expenseCode: '' },
  ]);
  // Saves the expenseCode per line index before the FA-clearing toggle overwrites it,
  // so unchecking can restore what the user had selected.
  const [prevExpenseCodeMap, setPrevExpenseCodeMap] = useState<Record<number, string>>({});

  // Entry-time duplicate check — fires once vendor + invoice no are entered.
  const dup = useApBillCheckDuplicate(companyId, {
    ...(vendorId ? { vendorId } : {}),
    ...(vendorInvoiceNo ? { invoiceNo: vendorInvoiceNo } : {}),
  });
  const dupMatches = dup.data?.matches ?? [];

  // EXPENSE accounts only. Postable leaf accounts are non-control (every real
  // account is a leaf with isControl=false); control/header accounts can't be
  // posted to, so we exclude them from the dropdown. Same rule as ar.tsx.
  const expenseAccounts = useMemo(() => {
    if (!coa) return [];
    return coa.filter((c) => c.type === 'EXPENSE' && c.isActive && !c.isControl);
  }, [coa]);

  // COA-resolved default expense code: first EXPENSE leaf; '' until COA loads.
  // Derived value — avoids a useEffect setter (lint: react-hooks/set-state-in-effect).
  // The select uses `l.expenseCode !== '' ? l.expenseCode : defaultExpenseCode` so the
  // default account is pre-selected visually once COA loads without mutating lines state.
  const defaultExpenseCode = expenseAccounts[0]?.code ?? '';

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unitPrice || 0), 0),
    [lines],
  );
  const vatAmount = useMemo(() => (subtotal * vatRate) / 100, [subtotal, vatRate]);
  const totalAmount = subtotal + vatAmount;

  // AP Phase C1 — the resolved term for the current selection (undefined = none).
  const selectedTerm = useMemo(
    () => paymentTerms.find((t) => t.code === paymentTermCode),
    [paymentTerms, paymentTermCode],
  );

  // Re-derive dueDate = billDate + netDays for a chosen term (kept overridable —
  // this only fires on an explicit term/vendor-default change, never on a manual
  // dueDate edit). Falls back to the current dueDate when no term resolves.
  function dueFromTerm(baseBillDate: string, term: PaymentTerm | undefined): string {
    if (!term) return dueDate;
    const base = new Date(baseBillDate);
    if (Number.isNaN(base.getTime())) return dueDate;
    base.setDate(base.getDate() + term.netDays);
    return dateToInputValue(base);
  }

  // Apply a term selection: snapshot the code + auto-set the due date.
  function chooseTerm(code: string) {
    setPaymentTermCode(code);
    const term = paymentTerms.find((t) => t.code === code);
    if (term) setDueDate(dueFromTerm(billDate, term));
  }

  // When the vendor changes, adopt its defaultPaymentTermCode (if it resolves to
  // an active preset) and auto-fill the due date — exactly what the server would
  // do on create, surfaced live so the user sees + can override it.
  function chooseVendor(id: string) {
    setVendorId(id);
    const vendor = vendors?.find((v) => v.id === id);
    const code = vendor?.defaultPaymentTermCode ?? '';
    const term = code ? paymentTerms.find((t) => t.code === code) : undefined;
    if (term) {
      setPaymentTermCode(term.code);
      setDueDate(dueFromTerm(billDate, term));
    }
  }

  function updateLine(idx: number, patch: Partial<CreateApBillLine>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  function addLine() {
    setLines((prev) => [
      ...prev,
      { description: '', qty: 1, unitPrice: 0, expenseCode: defaultExpenseCode },
    ]);
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
    // Rebuild the map without the removed index; shift indices above it down by 1.
    setPrevExpenseCodeMap((prev) => {
      const next: Record<number, string> = {};
      for (const [k, v] of Object.entries(prev)) {
        const n = Number(k);
        if (n < idx) next[n] = v;
        else if (n > idx) next[n - 1] = v;
        // n === idx is dropped
      }
      return next;
    });
  }

  async function handleSubmit() {
    // CIP Phase 4b — cipProjectId isn't on the shared CreateApBill type yet (the
    // API group lands the schema field in parallel); attach it via a local
    // extension so the payload is TS-safe and forward-compatible.
    const payload: CreateApBill & { cipProjectId?: string } = {
      billDate,
      dueDate,
      vendorId,
      ...(branchId ? { branchId } : {}),
      ...(departmentId ? { departmentId } : {}),
      ...(vendorInvoiceNo ? { vendorInvoiceNo } : {}),
      ...(description ? { description } : {}),
      // AP Phase C1 — send the chosen payment-term code so the server snapshots
      // the term + discount window onto the bill (and re-derives the due date).
      ...(paymentTermCode ? { paymentTermCode } : {}),
      // CIP Phase 4b — route the work-cost debit to CIP_CLEARING when tagged.
      ...(cipProjectId ? { cipProjectId } : {}),
      vatRate,
      postJv,
      lines: lines
        .filter((l) => l.description && Number(l.qty) > 0 && Number(l.unitPrice) >= 0)
        .map((l) => ({
          ...l,
          expenseCode: l.expenseCode !== '' ? l.expenseCode : defaultExpenseCode,
        })),
    };
    await create.mutateAsync({ data: payload, idempotencyKey });
    onClose();
  }

  const disabled =
    !vendorId || !billDate || !dueDate || lines.length === 0 || subtotal <= 0 || create.isPending;

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={modalBox}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>บันทึกใบสำคัญจ่าย</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={modalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={modalLabel}>ผู้ขาย</label>
              <select
                className="select"
                value={vendorId}
                onChange={(e) => chooseVendor(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                <option value="">— เลือกผู้ขาย —</option>
                {vendors?.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.code} · {v.nameTh}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={modalLabel}>สาขา</label>
              <BranchSelect
                companyId={companyId}
                value={branchId}
                onChange={setBranchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={modalLabel}>หน่วยงาน</label>
              <DepartmentSelect
                companyId={companyId}
                value={departmentId}
                onChange={setDepartmentId}
                branchId={branchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 120px', gap: 12 }}>
            <div>
              <label style={modalLabel}>วันที่บิล</label>
              <input
                type="date"
                className="input"
                value={billDate}
                onChange={(e) => {
                  setBillDate(e.target.value);
                  // Keep the due date in lock-step with the term when one is set.
                  if (selectedTerm) setDueDate(dueFromTerm(e.target.value, selectedTerm));
                }}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            {/* AP Phase C1 — payment-term preset picker (เงื่อนไขชำระ). Defaults
                from the vendor; choosing one auto-sets the due date below. */}
            <div>
              <label style={modalLabel}>เงื่อนไขชำระ</label>
              <SearchSelect
                ariaLabel="เงื่อนไขชำระ"
                value={paymentTermCode}
                onChange={chooseTerm}
                options={[
                  { value: '', label: '— ไม่ระบุ —' },
                  ...paymentTerms.map((t) => ({ value: t.code, label: `${t.code} · ${t.label}` })),
                ]}
                getLabel={(v) =>
                  v === ''
                    ? '— ไม่ระบุ —'
                    : (() => {
                        const t = paymentTerms.find((x) => x.code === v);
                        return t ? `${t.code} · ${t.label}` : v;
                      })()
                }
                placeholder="เลือกเงื่อนไขชำระ"
              />
            </div>
            <div>
              <label style={modalLabel}>กำหนดจ่าย</label>
              <input
                type="date"
                className="input"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={modalLabel}>VAT (%)</label>
              <input
                type="number"
                className="input tnum"
                value={vatRate}
                onChange={(e) => setVatRate(Number(e.target.value))}
                min={0}
                max={100}
                step={0.01}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
          </div>
          {/* AP Phase C1 — discount-window hint for the chosen term. */}
          {selectedTerm && toNumber(selectedTerm.discountPercent) > 0 && (
            <div
              data-testid="discount-window-hint"
              style={{
                fontSize: 13,
                color: 'var(--positive)',
                background: 'rgba(34,197,94,0.08)',
                border: '1px solid rgba(34,197,94,0.25)',
                borderRadius: 8,
                padding: '8px 12px',
                marginTop: -4,
              }}
            >
              <Icon name="check" size={13} /> ส่วนลด {toNumber(selectedTerm.discountPercent)}%
              ถ้าจ่ายใน {selectedTerm.discountDays} วัน
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={modalLabel}>เลขที่บิลผู้ขาย (optional)</label>
              <input
                className="input"
                value={vendorInvoiceNo}
                onChange={(e) => setVendorInvoiceNo(e.target.value)}
                placeholder="เช่น INV-2026-0042"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
              {dupMatches.length > 0 && (
                <DuplicateBanner matches={dupMatches} onOpenBill={onOpenBill} />
              )}
            </div>
            <div>
              <label style={modalLabel}>โหมดการบันทึก</label>
              <select
                className="select"
                value={postJv ? 'post' : 'pending'}
                onChange={(e) => setPostJv(e.target.value === 'post')}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                <option value="post">โพสต์ JV ทันที (POSTED)</option>
                <option value="pending">รออนุมัติ (PENDING — ไม่ลง GL)</option>
              </select>
            </div>
          </div>
          <div>
            <label style={modalLabel}>คำอธิบาย</label>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="เช่น ค่าวัตถุดิบ ใบสั่งซื้อ #..."
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          {/* CIP Phase 4b — optional CIP project tag. Leaving it blank keeps a
              normal bill (unchanged). Tagging routes the work-cost debit to
              CIP_CLEARING; the CIP page's กล่องรับเข้า (AP) then captures it. */}
          <div>
            <label style={modalLabel}>ผูกโครงการ CIP (งานระหว่างก่อสร้าง) — ไม่บังคับ</label>
            <SearchSelect
              ariaLabel="ผูกโครงการ CIP"
              value={cipProjectId}
              onChange={setCipProjectId}
              options={[
                { value: '', label: '— ไม่ผูกโครงการ —' },
                ...cipProjects.map((p) => ({
                  value: p.id,
                  label: `${p.voucherNo} · ${p.name}`,
                })),
              ]}
              getLabel={(v) =>
                v === ''
                  ? '— ไม่ผูกโครงการ —'
                  : (() => {
                      const p = cipProjects.find((x) => x.id === v);
                      return p ? `${p.voucherNo} · ${p.name}` : v;
                    })()
              }
              placeholder="เลือกโครงการ CIP (ถ้ามี)"
            />
          </div>

          <div>
            <label style={modalLabel}>รายการ</label>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>คำอธิบาย</th>
                    <th className="num" style={{ width: 80 }}>
                      จำนวน
                    </th>
                    <th className="num" style={{ width: 110 }}>
                      ราคา/หน่วย
                    </th>
                    <th style={{ width: 160 }}>บัญชีค่าใช้จ่าย</th>
                    <th style={{ width: 72, textAlign: 'center' }}>ทรัพย์สิน</th>
                    <th className="num" style={{ width: 110 }}>
                      ยอดเงิน
                    </th>
                    <th style={{ width: 30 }} />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const amount = Number(l.qty || 0) * Number(l.unitPrice || 0);
                    return (
                      <tr key={idx}>
                        <td>
                          <input
                            className="input"
                            value={l.description}
                            onChange={(e) => updateLine(idx, { description: e.target.value })}
                            style={{ width: '100%', height: 32, fontSize: 13 }}
                          />
                        </td>
                        <td className="num">
                          <input
                            type="number"
                            className="input tnum"
                            value={l.qty}
                            min={0}
                            step={0.0001}
                            onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })}
                            style={{
                              width: '100%',
                              height: 32,
                              fontSize: 13,
                              textAlign: 'right',
                            }}
                          />
                        </td>
                        <td className="num">
                          <input
                            type="number"
                            className="input tnum"
                            value={l.unitPrice}
                            min={0}
                            step={0.01}
                            onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) })}
                            style={{
                              width: '100%',
                              height: 32,
                              fontSize: 13,
                              textAlign: 'right',
                            }}
                          />
                        </td>
                        <td>
                          <select
                            className="select"
                            value={l.expenseCode !== '' ? l.expenseCode : defaultExpenseCode}
                            onChange={(e) => updateLine(idx, { expenseCode: e.target.value })}
                            disabled={l.expenseCode === FA_CLEARING_CODE}
                            style={{ width: '100%', height: 32, fontSize: 13 }}
                          >
                            {l.expenseCode === FA_CLEARING_CODE ? (
                              <option value={FA_CLEARING_CODE}>
                                พักรับทรัพย์สิน ({FA_CLEARING_CODE})
                              </option>
                            ) : (
                              <>
                                {expenseAccounts.length === 0 && (
                                  <option
                                    value={
                                      l.expenseCode !== '' ? l.expenseCode : defaultExpenseCode
                                    }
                                  >
                                    {l.expenseCode !== '' ? l.expenseCode : defaultExpenseCode}
                                  </option>
                                )}
                                {expenseAccounts.map((a) => (
                                  <option key={a.code} value={a.code}>
                                    {a.code} · {a.nameTh}
                                  </option>
                                ))}
                              </>
                            )}
                          </select>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <label
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 13,
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={l.expenseCode === FA_CLEARING_CODE}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setPrevExpenseCodeMap((prev) => ({
                                    ...prev,
                                    [idx]: l.expenseCode,
                                  }));
                                  updateLine(idx, { expenseCode: FA_CLEARING_CODE });
                                } else {
                                  updateLine(idx, {
                                    expenseCode: prevExpenseCodeMap[idx] ?? defaultExpenseCode,
                                  });
                                }
                              }}
                            />
                          </label>
                        </td>
                        <td className="num tnum">{fmt(amount, 2)}</td>
                        <td>
                          {lines.length > 1 && (
                            <button
                              className="btn ghost"
                              onClick={() => removeLine(idx)}
                              style={{ padding: '0 6px' }}
                            >
                              <Icon name="x" size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button className="btn ghost" onClick={addLine} style={{ marginTop: 8, fontSize: 13 }}>
              <Icon name="plus" size={12} /> เพิ่มรายการ
            </button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              rowGap: 4,
              fontSize: 14,
              marginTop: 8,
            }}
          >
            <div className="muted">ยอดก่อน VAT</div>
            <div className="tnum" style={{ textAlign: 'right' }}>
              {fmt(subtotal, 2)}
            </div>
            <div className="muted">VAT ({vatRate}%)</div>
            <div className="tnum" style={{ textAlign: 'right' }}>
              {fmt(vatAmount, 2)}
            </div>
            <div style={{ fontWeight: 600 }}>รวมทั้งสิ้น</div>
            <div className="tnum" style={{ textAlign: 'right', fontWeight: 600 }}>
              {fmt(totalAmount, 2)}
            </div>
          </div>

          {create.error && <div style={modalError}>บันทึกไม่สำเร็จ: {create.error.message}</div>}
        </div>
        <div style={modalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} />{' '}
            {create.isPending ? 'กำลังบันทึก…' : 'บันทึกใบสำคัญจ่าย'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// DuplicateBanner — inline warning shown in the create-bill modal when the
// read-only check-duplicate endpoint finds prior non-VOID bills for the same
// vendor + invoice no. Click a match to open its detail drawer (A1). Not a hard
// block — the server P2002→409 remains the guard.
// =============================================================================
function DuplicateBanner({
  matches,
  onOpenBill,
}: {
  matches: ApBillDuplicateMatch[];
  onOpenBill: (billId: string) => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--warning)',
        background: 'rgba(255,193,7,0.08)',
        borderRadius: 8,
        padding: '10px 12px',
        fontSize: 13,
        marginTop: 6,
      }}
    >
      <div style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: 4 }}>
        <Icon name="bell" size={13} /> อาจเป็นเอกสารซ้ำ ({matches.length})
      </div>
      {matches.map((m) => (
        <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => onOpenBill(m.id)}
            style={{
              color: 'var(--brand)',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              font: 'inherit',
              fontWeight: 500,
            }}
          >
            {m.voucherNo}
          </button>
          <span className="muted">
            {fmtDate(m.billDate)} · {fmt(toNumber(m.totalAmount), 2)} บาท · {m.status}
          </span>
        </div>
      ))}
    </div>
  );
}
